import React, { useEffect, useState } from "react";
import CardHeader from "../../components/common/CardHeader";
import AccessDenied from "../../components/common/AccessDenied";
import { AnnouncementBanner, ALLOWED_VARIANTS } from "../../components/common/LoginAnnouncement";
import { useAuth } from "../../context/AuthContext";
import {
  getLoginAnnouncementSettings,
  saveLoginAnnouncementSettings,
} from "../../services/SystemAnnouncement";

const EMPTY_ANNOUNCEMENT = {
  enabled: false,
  variant: "warning",
  title: "",
  message: "",
  linkLabel: "",
  linkUrl: "",
  openInNewTab: true,
  displayFrom: "",
  displayTo: "",
};

const VARIANT_LABELS = {
  info: "Neutralny komunikat",
  warning: "Ważna informacja",
  critical: "Pilny komunikat",
  success: "Pozytywna informacja",
};

const normalizeDateTime = (value) => (value ? value.slice(0, 16) : "");

const normalizeAnnouncement = (settings) => ({
  ...EMPTY_ANNOUNCEMENT,
  ...(settings || {}),
  displayFrom: normalizeDateTime(settings?.displayFrom),
  displayTo: normalizeDateTime(settings?.displayTo),
});

const hasContent = (announcement) =>
  Boolean(
    announcement.title?.trim() ||
      announcement.message?.trim() ||
      (announcement.linkLabel?.trim() && announcement.linkUrl?.trim())
  );

const getPreviewStatus = (announcement) => {
  const now = new Date();
  const displayFrom = announcement.displayFrom
    ? new Date(announcement.displayFrom)
    : null;
  const displayTo = announcement.displayTo ? new Date(announcement.displayTo) : null;

  if (!announcement.enabled) return "Wyłączony";
  if (!hasContent(announcement)) return "Brak treści";
  if (displayFrom && now < displayFrom) return "Zaplanowany";
  if (displayTo && now > displayTo) return "Zakończony";
  return "Aktywny";
};

function SystemSettings() {
  const { accessToken, checkPermission } = useAuth();
  const [announcement, setAnnouncement] = useState(EMPTY_ANNOUNCEMENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [announcementSectionOpen, setAnnouncementSectionOpen] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!accessToken || !checkPermission("Zarządzanie użytkownikami")) {
        setLoading(false);
        return;
      }

      try {
        setError("");
        const settings = await getLoginAnnouncementSettings(accessToken);
        setAnnouncement(normalizeAnnouncement(settings));
      } catch (err) {
        setError(
          err?.response?.data?.msg ||
            "Nie udało się pobrać ustawień komunikatu systemowego."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [accessToken, checkPermission]);

  const updateAnnouncement = (field, value) => {
    setSaved(false);
    setAnnouncement((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaved(false);
    setError("");

    if (
      announcement.displayFrom &&
      announcement.displayTo &&
      new Date(announcement.displayTo) < new Date(announcement.displayFrom)
    ) {
      setError("Data zakończenia nie może być wcześniejsza niż data rozpoczęcia.");
      return;
    }

    try {
      setSaving(true);
      const settings = await saveLoginAnnouncementSettings(
        announcement,
        accessToken
      );
      setAnnouncement(normalizeAnnouncement(settings));
      setSaved(true);
    } catch (err) {
      setError(
        err?.response?.data?.msg ||
          "Nie udało się zapisać komunikatu systemowego."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!checkPermission("Zarządzanie użytkownikami")) {
    return <AccessDenied />;
  }

  const previewStatus = getPreviewStatus(announcement);

  return (
    <>
      <CardHeader text="Ustawienia systemowe" onlyText="true" />
      <div className="container-fluid ms-4 px-4 py-3 w80">
        <div className="card shadow mb-3">
          <div className="card-header d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link text-decoration-none text-dark p-0 d-flex align-items-center"
              onClick={() =>
                setAnnouncementSectionOpen((current) => !current)
              }
              aria-expanded={announcementSectionOpen}
              aria-controls="system-announcement-section"
            >
              <i
                className={`bi ${
                  announcementSectionOpen ? "bi-chevron-down" : "bi-chevron-right"
                } me-2`}
              ></i>
              <strong>Komunikaty systemowe</strong>
            </button>
            <span className="badge bg-secondary ms-auto">{previewStatus}</span>
          </div>
          {announcementSectionOpen && (
          <div className="card-body" id="system-announcement-section">
            <p className="text-muted mb-3">
              Komunikat pojawi się na stronie logowania tylko wtedy, gdy jest
              włączony, ma uzupełnioną treść i aktualna data mieści się w
              wybranym zakresie.
            </p>
            {error && <div className="alert alert-danger">{error}</div>}
            {saved && <div className="alert alert-success">Zapisano komunikat.</div>}
            {loading ? (
              <div className="text-muted">Ładowanie...</div>
            ) : (
              <>
                <div className="form-check form-switch mb-3">
                  <input
                    id="system-announcement-enabled"
                    className="form-check-input"
                    type="checkbox"
                    checked={announcement.enabled}
                    onChange={(event) =>
                      updateAnnouncement("enabled", event.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="system-announcement-enabled"
                  >
                    Włącz komunikat na stronie logowania
                  </label>
                  <div className="form-text">
                    Wyłączenie ukrywa komunikat natychmiast, bez usuwania
                    wpisanej treści i dat.
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-lg-8">
                    <label className="form-label">Tytuł</label>
                    <input
                      className="form-control"
                      maxLength="200"
                      value={announcement.title}
                      onChange={(event) =>
                        updateAnnouncement("title", event.target.value)
                      }
                    />
                  </div>
                  <div className="col-12 col-lg-4">
                    <label className="form-label">Wariant</label>
                    <select
                      className="form-control form-select"
                      value={announcement.variant}
                      onChange={(event) =>
                        updateAnnouncement("variant", event.target.value)
                      }
                    >
                      {Array.from(ALLOWED_VARIANTS).map((variant) => (
                        <option key={variant} value={variant}>
                          {VARIANT_LABELS[variant]}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Wariant zmienia kolor i wagę komunikatu.
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Treść</label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={announcement.message}
                      onChange={(event) =>
                        updateAnnouncement("message", event.target.value)
                      }
                    />
                  </div>

                  <div className="col-12 col-lg-6">
                    <label className="form-label">Tekst linku</label>
                    <input
                      className="form-control"
                      maxLength="100"
                      value={announcement.linkLabel}
                      onChange={(event) =>
                        updateAnnouncement("linkLabel", event.target.value)
                      }
                    />
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Adres linku</label>
                    <input
                      className="form-control"
                      maxLength="500"
                      value={announcement.linkUrl}
                      onChange={(event) =>
                        updateAnnouncement("linkUrl", event.target.value)
                      }
                    />
                  </div>

                  <div className="col-12">
                    <div className="form-check">
                      <input
                        id="system-announcement-new-tab"
                        className="form-check-input"
                        type="checkbox"
                        checked={announcement.openInNewTab}
                        onChange={(event) =>
                          updateAnnouncement("openInNewTab", event.target.checked)
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="system-announcement-new-tab"
                      >
                        Otwieraj link w nowej karcie
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <label className="form-label">Wyświetlaj od</label>
                    <input
                      className="form-control"
                      type="datetime-local"
                      value={announcement.displayFrom}
                      onChange={(event) =>
                        updateAnnouncement("displayFrom", event.target.value)
                      }
                    />
                    <div className="form-text">
                      Zostaw puste, jeśli komunikat ma być widoczny od razu po
                      zapisaniu.
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Wyświetlaj do</label>
                    <input
                      className="form-control"
                      type="datetime-local"
                      value={announcement.displayTo}
                      onChange={(event) =>
                        updateAnnouncement("displayTo", event.target.value)
                      }
                    />
                    <div className="form-text">
                      Zostaw puste, jeśli komunikat ma pozostać aktywny bez
                      daty końcowej.
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label">Podgląd</label>
                  {hasContent(announcement) ? (
                    <AnnouncementBanner announcement={announcement} />
                  ) : (
                    <div className="border rounded-3 p-3 text-muted">
                      Brak treści komunikatu.
                    </div>
                  )}
                </div>

                <div className="d-flex mt-4">
                  <button
                    type="button"
                    className="btn btn-primary ms-auto"
                    disabled={saving}
                    onClick={handleSave}
                  >
                    {saving ? "Zapisywanie..." : "Zapisz komunikat"}
                  </button>
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SystemSettings;
